<template>
  <div class="flex flex-col p-6 ml-4 lg:ml-64">
    <h2 class="text-xl text-gray-900 font-semibold mb-4">
      Gerenciamento de Perguntas e Respostas
    </h2>
    <!-- Campo de seleção de controle -->
    <div class="mb-6">
      <h3 class="text-lg font-semibold">Controles</h3>
      <ul class="border rounded p-4 bg-white">
        <li
          v-for="control in controllers"
          :key="control._id"
          class="py-2 flex items-center justify-between"
        >
          <label
            class="flex-1 flex items-center justify-between p-3 rounded cursor-pointer border transition-colors duration-200"
            :class="{
              'bg-blue-600 text-white': selectedControllerId === control._id,
              'bg-gray-100 text-gray-800': selectedControllerId !== control._id,
            }"
          >
            <!-- Hidden radio input -->
            <input
              type="radio"
              :value="control._id"
              v-model="selectedControllerId"
              @click="openModal(control._id, control.ControllerName)"
              class="hidden"
            />
            <span class="font-medium">
              {{ control.ControllerName }} ({{ control.TypesOfControls }})
            </span>

            <!-- Status Icon -->
            <span>
              <span v-if="hasQuestions(control._id)">✔️</span>
            </span>
          </label>
        </li>
      </ul>
    </div>

    <!-- Modal de Adição/Edição -->
    <div
      v-if="modalAberto"
      class="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center ml-70"
    >
      <div
        class="bg-white p-6 rounded shadow-lg w-[100%] h-[100%] relative overflow-auto"
      >
        <button class="absolute top-2 right-2 text-xl" @click="closedModal">
          &times;
        </button>
        <h2 class="text-lg font-semibold mb-2">
          {{ isEditing ? 'Editar Pergunta' : 'Adicionar Pergunta' }}
        </h2>

        <p class="text-sm text-gray-600 mb-4">
          Controle Selecionado: <strong>{{ selectedControllerName }}</strong>
        </p>

        <label class="block text-sm font-medium">Pergunta</label>
        <textarea
          v-model="formData.Question"
          class="w-full p-2 border border-gray-300 rounded mb-4 resize-none"
          required
          :style="{
            minWidth: '750px',
            maxWidth: '100%',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            height: 'auto',
          }"
          rows="1"
          @input="adjustHeight($event)"
        >
        </textarea>

        <label class="block text-sm font-medium">Objetivo da Pergunta</label>
        <textarea
          v-model="formData.Option"
          class="w-full p-2 border border-gray-300 rounded mb-4 resize-none"
          required
          :style="{
            minWidth: '750px',
            maxWidth: '100%',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            height: 'auto',
          }"
          rows="1"
          @input="adjustHeight($event)"
        >
        </textarea>

        <label class="block text-sm font-medium">Respostas</label>
        <div
          v-for="(answer, index) in formData.Answers"
          :key="index"
          class="flex mb-2"
        >
          <input
            v-model="formData.Answers[index]"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none"
            type="text"
            name="answer"
            autocomplete="on"
          />
          <button @click="removeAnswer(index)" class="ml-2 text-red-500">
            ❌
          </button>
        </div>
        <button @click="addAnswer" class="text-blue-500 text-sm">
          + Adicionar resposta
        </button>

        <div class="flex justify-end mt-4">
          <button
            @click="closedModal"
            class="px-4 py-2 bg-gray-300 rounded mr-2"
          >
            Cancelar
          </button>
          <button
            @click="addAnotherQuestion"
            class="px-4 py-2 bg-green-500 text-white rounded"
          >
            + Adicionar Outra Pergunta
          </button>
          <button
            @click="isEditing ? updateExistingQuestion() : createNewQuestion()"
            class="px-4 py-2 bg-blue-500 text-white rounded ml-2"
          >
            {{ isEditing ? 'Atualizar' : 'Criar' }}
          </button>
        </div>

        <!-- Tabela de listagem das perguntas -->
        <div class="mt-6 space-y-4">
          <div
            v-for="question in filteredQuestions"
            :key="question._id"
            class="bg-white shadow-md rounded-lg p-4 border border-gray-300"
          >
            <!-- Pergunta -->
            <div class="text-gray-800 font-semibold text-base mb-2">
              {{ question.Question[0] }}
            </div>

            <!-- Option -->
            <div class="text-gray-800 text-sm mb-2">
              <span class="text-sm font-bold mr-1">Objetivo da Pergunta:</span
              >{{ question.Option }}
            </div>

            <!-- Respostas -->
            <ul
              class="list-inside text-gray-700 text-sm space-y-8 leading-relaxed"
            >
              <li
                v-for="(answer, index) in question.Answers"
                :key="index"
                v-html="formatAnswerWithStatus(answer, index)"
              ></li>
            </ul>

            <!-- Ações -->
            <div class="mt-4 flex gap-3">
              <button
                class="px-3 py-1 hover:bg-yellow-400 transition rounded text-xs font-medium text-gray-800"
                @click="handleEdit(question)"
              >
                ✏️ Editar
              </button>
              <button
                class="px-3 py-1 hover:bg-red-600 mpx-3 transition rounded text-xs font-medium text-gray-800"
                @click="confirmRemoval(question._id)"
              >
                🗑️ Remover
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getControllers } from '@/services/security-controls/api.controllers'
import {
  createQuestion,
  deleteQuestion,
  getQuestionsByControllerId,
  updateQuestion,
} from '@/services/qa/api.question.js'

export default {
  data() {
    return {
      questions: [],
      controllers: [],
      selectedControllerId: null,
      modalAberto: false,
      isEditing: false,
      formData: {
        ControllerID: '',
        ControllerName: '',
        Option: '',
        Question: '',
        Answers: [''],
      },
      editingId: null,
    }
  },

  computed: {
    selectedControllerName() {
      const controller = this.controllers.find(
        (c) => c._id === this.selectedControllerId
      )
      return controller ? controller.ControllerName : 'Nenhum selecionado'
    },
    filteredQuestions() {
      return this.questions.filter(
        (question) => question.ControllerID === this.selectedControllerId
      )
    },
  },

  watch: {
    async selectedControllerId(newControllerId) {
      if (newControllerId) {
        await this.fetchQuestions(newControllerId)
      }
    },
  },

  methods: {
    formatAnswerWithStatus(answer, index) {
      const statusLabels = [
        '✅ Implementada',
        '⚠️ Parcialmente Implementada',
        '❌ Não Implementada',
        '➖ Não se Aplica',
      ]

      const statusMap = {
        '✅ Implementada': 'text-green-600 ',
        '⚠️ Parcialmente Implementada': 'text-yellow-500',
        '❌ Não Implementada': 'text-red-600',
        '➖ Não se Aplica': 'text-gray-500',
      }

      const statusLabel = statusLabels[index] || ''
      const statusClass = statusMap[statusLabel] || ''

      const formattedSentences = answer
        .split('.')
        .map((sentence) => sentence.trim())
        .filter((sentence) => sentence.length > 0)
        .map((sentence) => `&bull; ${sentence}.`)
        .join('<br>')

      const result = statusLabel
        ? `<span class="${statusClass} font-semibold">${statusLabel}</span><br>${formattedSentences}`
        : formattedSentences

      return result
    },

    adjustHeight(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },

    hasQuestions(controllerId) {
      return this.questions.some((q) => q.ControllerID === controllerId)
    },

    async updateExistingQuestion() {
      try {
        this.formData.Answers = this.formData.Answers.filter(
          (answer) => answer.trim() !== ''
        )
        if (!this.editingId) {
          alert('Erro: ID da pergunta não encontrado!')
          return
        }

        await updateQuestion(this.editingId, this.formData)
        await this.fetchQuestions(this.formData.ControllerID)
        this.formData.Question = ''
        this.formData.Answers = ['']
        this.formData.Option = ['']
      } catch (error) {
        console.error('Erro ao atualizar pergunta:', error)
      }
    },
    async fetchQuestions(controllerId) {
      try {
        this.questions = await getQuestionsByControllerId(controllerId)
      } catch (error) {
        console.error('Erro ao buscar perguntas:', error)
      }
    },

    async fetchControllers() {
      try {
        this.controllers = await getControllers()
      } catch (error) {
        console.error('Erro ao buscar controles:', error)
      }
    },

    openModal(controlId, controlName) {
      this.modalAberto = true
      this.isEditing = false
      this.formData = {
        ControllerID: controlId,
        ControllerName: controlName,
        Question: '',
        Answers: [''],
      }
    },

    closedModal() {
      this.modalAberto = false
      this.editingId = null
      this.formData = {
        ControllerID: '',
        ControllerName: '',
        Option: '',
        Question: '',
        Answers: [''],
      }
    },

    handleEdit(question) {
      this.modalAberto = true
      this.isEditing = true
      this.editingId = question._id
      this.formData = { ...question }
    },

    async createNewQuestion() {
      try {
        this.formData.Answers = this.formData.Answers.filter(
          (answer) => answer.trim() !== ''
        )
        if (!this.formData.ControllerID) {
          alert('Selecione um controle!')
          return
        }
        this.formData.Question = ''
        this.formData.Answers = ['']
        this.formData.Option = ['']

        await createQuestion(this.formData)
        await this.fetchQuestions(this.formData.ControllerID)
        this.closedModal()
      } catch (error) {
        console.error('Erro ao criar pergunta:', error)
      }
    },

    async addAnotherQuestion() {
      try {
        this.formData.Answers = this.formData.Answers.filter(
          (answer) => answer.trim() !== ''
        )
        if (!this.formData.ControllerID) {
          alert('Selecione um controle!')
          return
        }

        await createQuestion(this.formData)
        await this.fetchQuestions(this.formData.ControllerID)

        this.formData.Question = ''
        this.formData.Answers = ['']
        this.formData.Option = ['']
      } catch (error) {
        console.error('Erro ao adicionar pergunta:', error)
      }
    },

    async confirmRemoval(id) {
      if (confirm('Tem certeza que deseja remover esta pergunta?')) {
        try {
          await deleteQuestion(id)
          const updated = await getQuestionsByControllerId(
            this.selectedControllerId
          )
          this.questions = [...updated]
        } catch (error) {
          console.error('Erro ao remover pergunta:', error)
        }
      }
    },

    addAnswer() {
      this.formData.Answers.push('')
    },

    removeAnswer(index) {
      this.formData.Answers.splice(index, 1)
    },
  },

  mounted() {
    this.fetchControllers()
  },
}
</script>
